var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { FormattedNumericInput, PasswordInput, RadioGroup, Select, SSNInput, SSNInput2, ToggleButtonGroup, FirstNameInput, LastNameInput, DateInput, DateInputSeparateFields, TelephoneInput, TextInputWithValidationCheck, EmailInput, } from '@pinwheel/origami';
import React from 'react';
import { useAppSelector } from '../../../state';
import { selectFeatureFlags } from '../../../state/slices/slice-link-params';
import { SCREEN_TITLE_HEADER_DEFAULT_ID } from '../../../components/ScreenTitleHeader';
function InputField(_a) {
    var _b;
    var field = _a.field, fieldValues = _a.fieldValues, setFieldValues = _a.setFieldValues, error = _a.error, index = _a.index, fieldCount = _a.fieldCount;
    var key = field.key, label = field.label, hint = field.hint;
    var featureFlags = useAppSelector(selectFeatureFlags);
    var hideShowPassword = featureFlags.hideShowPassword;
    var shouldAutoFocus = index === 0;
    var commonProps = {
        value: fieldValues[key] || field.initialValue || '',
        label: label,
        error: error,
        hint: hint
            ? {
                label: hint.label,
                // Note `content` is legacy. Adding this for backwards compatibility practice.
                // @ts-ignore
                content: hint.content || hint.textList,
            }
            : undefined,
        autoFocus: shouldAutoFocus,
        // Normally, for screen reader accessibility, the screen title header is
        // focused so that navigation starts at the beginning of the screen. If we
        // apply autofocus here, set `aria-describedby` pointed to that header so
        // that the user doesn't lose out on context.
        'aria-describedby': shouldAutoFocus
            ? SCREEN_TITLE_HEADER_DEFAULT_ID
            : undefined,
        eventId: field.key,
        // WCAG: Mark all fields as required. This applies the `required` attribute
        // for supported components (i.e. <input> and <select>), and `aria-required`
        // for everything else.
        required: true,
        'aria-required': true,
        readOnly: field.lockedField,
    };
    // Note `type` is legacy. Adding this for backwards compatibility practice.
    if ('type' in field) {
        // @ts-ignore
        field.fieldType = field.type;
    }
    var _onChange = function (v) {
        var _a;
        return setFieldValues(__assign(__assign({}, fieldValues), (_a = {}, _a[key] = v, _a)));
    };
    var onChange = function (v) { return _onChange(v); };
    var onChangeSelect = function (_, v) { return _onChange(v); };
    if (field.fieldType === 'text') {
        Object.assign(commonProps, { placeholder: field.placeholder });
        if (field.fieldSubtype === 'email') {
            return React.createElement(EmailInput, __assign({}, commonProps, { onChange: onChange }));
        }
        if (field.fieldSubtype === 'tel') {
            return (React.createElement(TelephoneInput, __assign({}, commonProps, { onChange: onChange, initialValue: (_b = field.initialValue) !== null && _b !== void 0 ? _b : '' })));
        }
        if (field.fieldSubtype === 'ssn' ||
            field.key === 'social_security_number') {
            if (field.lockedField && field.initialValue) {
                return (React.createElement(SSNInput2, __assign({}, commonProps, { onChange: onChange, initialValue: field.initialValue })));
            }
            return React.createElement(SSNInput, __assign({}, commonProps, { onChange: onChange }));
        }
        if (field.shouldObfuscate)
            return (React.createElement(PasswordInput, __assign({}, commonProps, { onChange: onChange, hideShowPassword: hideShowPassword })));
        if (field.key === 'first_name')
            return React.createElement(FirstNameInput, __assign({}, commonProps, { onChange: onChange }));
        if (field.key === 'last_name')
            return React.createElement(LastNameInput, __assign({}, commonProps, { onChange: onChange }));
        return (React.createElement(TextInputWithValidationCheck, __assign({}, commonProps, { onChange: onChange, autoCapitalize: "off", pattern: field.pattern, minLength: field.minLength, maxLength: field.maxLength })));
    }
    if (field.fieldType === 'date') {
        return (React.createElement(DateInput, __assign({}, commonProps, { onChange: onChange, patternType: "long" })));
    }
    if (field.fieldType === 'date3Inputs') {
        return React.createElement(DateInputSeparateFields, __assign({}, commonProps, { onChange: onChange }));
    }
    if (field.fieldType === 'number') {
        return (React.createElement(FormattedNumericInput, __assign({}, commonProps, { onChange: onChange })));
    }
    if (field.fieldType === 'toggleButtonGroup') {
        var _field = field;
        return (React.createElement(ToggleButtonGroup, __assign({}, commonProps, { options: _field.options, onChange: onChange })));
    }
    if (field.fieldType === 'select_single') {
        return fieldCount > 1 ? (React.createElement(Select, __assign({}, commonProps, { options: field.options, onChange: onChangeSelect }))) : (React.createElement(RadioGroup, __assign({}, commonProps, { options: field.options, onChange: onChange })));
    }
    return null;
}
export default InputField;
